import React, { useState } from "react";

function getMaxDate() {
  const d = new Date();
  d.setMonth(d.getMonth() + 6);
  return d.toISOString().split("T")[0];
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" });
}

function AmendModal({ contractor, managerEmail, onClose, onSuccess }) {
  const [newDate, setNewDate] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [error, setError] = useState("");
  const [poError, setPoError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [ticketNumber, setTicketNumber] = useState(null);

  const getMinDate = () => new Date().toISOString().split("T")[0];

  const handleDateChange = (val) => {
    setNewDate(val);
    if (val && val < getMinDate()) {
      setError("New end date cannot be in the past, please submit as termination.");
    } else if (val && val > getMaxDate()) {
      setError("New end date cannot be more than 6 months from today.");
    } else {
      setError("");
    }
  };

  const handleSubmit = async () => {
    if (!newDate || error) return;
    if (!poNumber.trim()) {
      setPoError("PO number is required to raise a ServiceDesk ticket.");
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch('/api/raiseTicket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'amend',
          contractorEmail: contractor.email,
          contractorName: contractor.name,
          managerEmail: managerEmail,
          newDate,
          poNumber,
        })
      });
      if (!response.ok) throw new Error('Failed to raise ticket');
      const data = await response.json();
      setTicketNumber(data.ticketNumber);
      setSubmitted(true);
      setTimeout(() => onSuccess(), 1000);
      if (onSuccess) onSuccess(contractor.email, data.ticketNumber);
    } catch (err) {
      setSubmitError('Something went wrong raising the ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const overlayStyle = { position:"fixed", inset:0, background:"rgba(24,7,58,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:100, backdropFilter:"blur(4px)" };
  const modalStyle = { background:"#fff", borderRadius:"10px", padding:"28px", width:"440px", boxShadow:"0 20px 60px rgba(24,7,58,0.2)" };
  const labelStyle = { fontSize:"10px", fontWeight:700, color:"#4d4777", marginBottom:"5px", textTransform:"uppercase", letterSpacing:"0.05em", display:"block" };
  const inputStyle = { width:"100%", padding:"9px 12px", border:"0.5px solid #e0e0df", borderRadius:"6px", fontSize:"12px", color:"#18073a", background:"#fff", fontFamily:"Montserrat, sans-serif" };
  const disabledInputStyle = { ...inputStyle, background:"#f5f5f3", color:"#b0adc8", borderColor:"transparent" };

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle}>
        <div style={{ fontSize:"16px", fontWeight:700, color:"#18073a", marginBottom:"3px" }}>Amend end date</div>
        <div style={{ fontSize:"12px", color:"#79749c", marginBottom:"22px" }}>{contractor.name} · {contractor.jobTitle}</div>
        <div style={{ marginBottom:"14px" }}>
          <label style={labelStyle}>Current end date</label>
          <input style={disabledInputStyle} value={formatDate(contractor.endDate)} disabled />
        </div>
        <div style={{ marginBottom:"14px" }}>
          <label style={labelStyle}>New end date <span style={{ color:"#b0adc8", fontWeight:400, textTransform:"none", letterSpacing:0 }}>— max 6 months from today</span></label>
          <div style={{ display:"flex", gap:"8px", marginBottom:"6px" }}>
            <button onClick={() => handleDateChange(getMaxDate())} style={{ padding:"5px 10px", fontSize:"11px", fontWeight:600, border:"0.5px solid #e0e0df", borderRadius:"5px", background:"#f0f0ef", color:"#18073a", cursor:"pointer", fontFamily:"Montserrat, sans-serif" }}>
              + 6 months
            </button>
          </div>
          <input style={inputStyle} type="date" value={newDate} min={getMinDate()} max={getMaxDate()} onChange={e => handleDateChange(e.target.value)} />
          {error && <div style={{ fontSize:"11px", color:"#82162d", marginTop:"5px", fontWeight:500 }}>{error}</div>}
        </div>
        <div style={{ marginBottom:"14px" }}>
          <label style={labelStyle}>Purchase order number</label>
          <input
            style={inputStyle}
            type="text"
            placeholder="e.g. IES-2026-00351"
            value={poNumber}
            onChange={e => { setPoNumber(e.target.value); if (e.target.value.trim()) setPoError(""); }}
          />
          {poError && <div style={{ fontSize:"11px", color:"#82162d", marginTop:"5px", fontWeight:500 }}>{poError}</div>}
        </div>
        {!submitted && (
          <div style={{ background:"#f5f5f3", borderLeft:"3px solid #498ca7", borderRadius:"0 6px 6px 0", padding:"11px 13px", fontSize:"11px", color:"#4d4777", lineHeight:1.7, marginBottom:"14px" }}>
            A ServiceDesk ticket will be raised with IT to action this change. Once approved the updated date will appear in this portal.
          </div>
        )}
        {submitError && (
          <div style={{ fontSize:"11px", color:"#82162d", marginBottom:"8px", fontWeight:500 }}>{submitError}</div>
        )}
        {submitted ? (
          <div style={{ marginTop:"4px" }}>
            <div style={{ background:"#e8ede7", borderLeft:"3px solid #677a64", borderRadius:"0 6px 6px 0", padding:"11px 13px", fontSize:"12px", color:"#3a4838", marginBottom:"16px" }}>
              Request submitted successfully. {ticketNumber && `Ticket ${ticketNumber} has been raised.`}
            </div>
            <div style={{ display:"flex", justifyContent:"flex-end" }}>
              <button onClick={onClose} style={{ padding:"9px 16px", fontSize:"12px", fontWeight:600, color:"#79749c", background:"#f0f0ef", border:"none", borderRadius:"6px", cursor:"pointer", fontFamily:"Montserrat, sans-serif" }}>Close</button>
            </div>
          </div>
        ) : (
          <div style={{ display:"flex", gap:"8px", justifyContent:"flex-end" }}>
            <button onClick={onClose} style={{ padding:"9px 16px", fontSize:"12px", fontWeight:600, color:"#79749c", background:"#f0f0ef", border:"none", borderRadius:"6px", cursor:"pointer", fontFamily:"Montserrat, sans-serif" }}>Cancel</button>
            <button onClick={handleSubmit} disabled={!newDate || !!error || submitting} style={{ padding:"9px 20px", fontSize:"12px", fontWeight:700, background: (!newDate || !!error || submitting) ? "#b0adc8" : "#18073a", color:"#fff", border:"none", borderRadius:"6px", cursor: (!newDate || !!error || submitting) ? "not-allowed" : "pointer", fontFamily:"Montserrat, sans-serif" }}>
              {submitting ? "Submitting…" : "Submit request"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AmendModal;